import * as React from "react";
import type { SAAgent } from "@/data/sellerAnalysisTypes";
import { Reveal, Overline } from "@/components/presentation/primitives";

interface Props {
  agent: SAAgent;
}

export const SA_AgentStory: React.FC<Props> = ({ agent }) => {
  if (!agent?.story && !agent?.storyHeadline) return null;
  const paragraphs = agent.story ? agent.story.split("\n\n") : [];

  return (
    <section
      className="relative w-full overflow-hidden bg-charcoal py-24 md:py-32"
      data-testid="sa-agent-story"
      aria-label="Why work with Alain"
    >
      <div className="absolute inset-0 grain-overlay opacity-[0.07]" />
      <div className="relative mx-auto grid w-full max-w-5xl grid-cols-1 gap-12 px-6 md:px-10 lg:grid-cols-[280px_1fr] lg:items-start lg:gap-16">
        {agent.photoUrl && (
          <Reveal>
            <figure className="overflow-hidden">
              <img
                src={agent.photoUrl}
                alt={agent.name}
                loading="eager"
                decoding="async"
                className="aspect-[4/5] w-full object-cover object-top"
              />
            </figure>
          </Reveal>
        )}

        <Reveal delay={0.1}>
          <Overline className="text-ivory/50">Why work with {agent.name.split(" ")[0]}</Overline>
          {agent.storyHeadline && (
            <h2 className="mt-4 font-serif text-3xl leading-tight text-ivory md:text-4xl">
              {agent.storyHeadline}
            </h2>
          )}
          <div className="mt-6 space-y-4 text-base leading-relaxed text-ivory/80 md:text-lg">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <div className="mt-9 border-t border-ivory/15 pt-7">
            <p className="font-serif text-xl text-ivory">{agent.name}</p>
            <p className="mt-1 text-xs uppercase tracking-[0.16em] text-ivory/60">
              {[agent.title, agent.brokerage].filter(Boolean).join(" · ")}
            </p>
            {agent.team && (
              <p className="mt-0.5 text-xs uppercase tracking-[0.16em] text-brass">
                {agent.team}
              </p>
            )}
            {agent.credentials?.length ? (
              <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                {agent.credentials.map((c, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-2 text-sm text-ivory/70"
                  >
                    <span aria-hidden className="h-1 w-1 rounded-full bg-brass" />
                    {c}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
